import type { RefObject } from "react";
import { useEffect, useState } from "react";

export function useIsSticky(ref: RefObject<HTMLElement>): boolean {
  const [isSticky, setIsSticky] = useState(false);

  useEffect(() => {
    const el = ref.current;

    if (!el) return undefined;

    const updateSticky = () => {
      if (!ref.current) return;
      // Re-read on every evaluation so CSS variable changes (e.g. --omnisearch-height)
      // are always reflected rather than using a stale mount-time snapshot.
      const stickyTop = parseFloat(getComputedStyle(ref.current).top) || 0;

      setIsSticky(window.scrollY > 0 && ref.current.getBoundingClientRect().top <= stickyTop);
    };

    window.addEventListener("scroll", updateSticky, { passive: true });

    // Re-evaluate immediately when any inline CSS variable on :root changes
    // (e.g. --omnisearch-height toggled by the search bar).
    const mutationObserver = new MutationObserver(updateSticky);

    mutationObserver.observe(document.documentElement, {
      attributeFilter: ["style"],
      attributes: true,
    });

    return () => {
      window.removeEventListener("scroll", updateSticky);
      mutationObserver.disconnect();
    };
  }, [ref]);

  return isSticky;
}
